import { FC, useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Box, Grid, Tab, Tabs } from '@mui/material'
import { PageHeader } from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { dashboardService } from '@/services/dashboardService'
import { DashboardResumo } from '@/types/dashboard'
import { totalDaFase } from '@/types/statusReserva'
import OperacoesPage from './OperacoesPage'
import Visao360Page from './Visao360Page'
import ClientesListPage from '@/pages/clientes/ClientesListPage'

type Aba = 'visao360' | 'operacoes' | 'clientes'

const lerAba = (valor: string | null): Aba =>
  valor === 'clientes' || valor === 'operacoes' ? valor : 'visao360'

const formatBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)

/**
 * Operações e Clientes (Figma 26:134, Onda 2 / A04) — página única com abas.
 *
 * Onda 2 entrou o padrão (PageHeader v2 + 4 KPIs + abas sobre as listas existentes);
 * a Onda 8 / A16 acrescenta a aba padrão "Visão 360" — uma linha por reserva/empresa com
 * o Status 360 derivado (Em dia · Atenção · Crítico) dos campos que a lista já entrega.
 * `?aba=clientes` (redirect do A02 em `/clientes`) e `?aba=operacoes` continuam honrados;
 * sem `?aba=` abre a Visão 360.
 *
 * KPIs do resumo existente (`GET /v1/dashboard/resumo`) — mesmo agregado do Kanban e do
 * Dashboard; o agrupamento por fase é apresentação (mesmo `totalDaFase` do Kanban).
 */
const OperacoesClientesPage: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const aba = lerAba(searchParams.get('aba'))

  const [resumo, setResumo] = useState<DashboardResumo | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [estadoErro, setEstadoErro] = useState<'erro' | 'semPermissao'>('erro')

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      setResumo(await dashboardService.getResumo())
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar o resumo')
      setEstadoErro((e as { status?: number })?.status === 403 ? 'semPermissao' : 'erro')
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const trocarAba = (nova: Aba) => {
    if (nova === 'visao360') searchParams.delete('aba')
    else searchParams.set('aba', nova)
    setSearchParams(searchParams, { replace: true })
  }

  const porStatus = resumo?.pipeline.porStatus ?? {}

  return (
    <Box>
      <PageHeader
        title="Operações e Clientes"
        subtitle="Operações contratadas e a carteira de clientes por trás delas"
      />

      {erro ? (
        <EstadoDeLista
          estado={estadoErro}
          {...(estadoErro === 'erro' && {
            mensagem: 'Não foi possível carregar os indicadores',
            detalhe: erro,
            acao: { label: 'Tentar novamente', onClick: () => void carregar() },
          })}
          minHeight={160}
        />
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            {/* fonte: GET /v1/dashboard/resumo (pipeline.porStatus, fase Contratação/Operação) */}
            <KpiCard
              label="Operações"
              valor={resumo ? totalDaFase(porStatus, 'Contratação / Operação') : '—'}
              legenda="Contratadas e ativas"
              variante="escura"
              carregando={carregando}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {/* fonte: GET /v1/dashboard/resumo (financeiro.volumeFechado) */}
            <KpiCard
              label="Volume fechado"
              valor={resumo ? formatBRL(resumo.financeiro.volumeFechado) : '—'}
              carregando={carregando}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {/* fonte: GET /v1/dashboard/resumo (financeiro.volumeEmNegociacao) */}
            <KpiCard
              label="Em negociação"
              valor={resumo ? formatBRL(resumo.financeiro.volumeEmNegociacao) : '—'}
              carregando={carregando}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {/* fonte: GET /v1/dashboard/resumo (conversao — ganhas ÷ total do recorte) */}
            <KpiCard
              label="Conversão"
              valor={resumo?.conversao != null ? `${resumo.conversao}%` : '—'}
              legenda="Ganhas sobre o total"
              carregando={carregando}
            />
          </Grid>
        </Grid>
      )}

      <Tabs value={aba} onChange={(_, v: Aba) => trocarAba(v)} sx={{ mb: 2 }}>
        <Tab label="Visão 360" value="visao360" />
        <Tab label="Operações" value="operacoes" />
        <Tab label="Clientes" value="clientes" />
      </Tabs>

      {aba === 'visao360' && <Visao360Page embutida />}
      {aba === 'operacoes' && <OperacoesPage embutida />}
      {aba === 'clientes' && <ClientesListPage embutida />}
    </Box>
  )
}

export default OperacoesClientesPage
